import { createContext, useContext, useState, ReactNode } from 'react';
import portalApi, { PORTAL_TOKEN_KEY, PORTAL_ACCOUNT_KEY, apiError } from '../pages/QuestionnaireResponse/portalApi';

// Session for the vendor recipient portal -- deliberately separate from ../context/AuthContext.tsx
// (different localStorage keys, its own React context) so a recipient's portal session can never
// collide with a tenant user's own logged-in session in the same browser.

export interface PortalAccount {
  id: string;
  email: string;
  name: string | null;
}

interface PortalAuthContextValue {
  account: PortalAccount | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string, account: PortalAccount) => void;
  updateAccount: (account: PortalAccount) => void;
  logout: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextValue | undefined>(undefined);

function readStoredAccount(): PortalAccount | null {
  try {
    const raw = localStorage.getItem(PORTAL_ACCOUNT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<PortalAccount | null>(() =>
    localStorage.getItem(PORTAL_TOKEN_KEY) ? readStoredAccount() : null
  );

  const loginWithToken = (accessToken: string, account: PortalAccount) => {
    localStorage.setItem(PORTAL_TOKEN_KEY, accessToken);
    localStorage.setItem(PORTAL_ACCOUNT_KEY, JSON.stringify(account));
    setAccount(account);
  };

  const login = async (email: string, password: string) => {
    try {
      const { data } = await portalApi.post('/v1/questionnaire-responses/portal/login', { email, password });
      loginWithToken(data.access_token, data.account);
    } catch (error) {
      throw new Error(apiError(error, 'Could not sign in. Check your email and password.'));
    }
  };

  // After a profile edit (e.g. a rename on the Account page) -- keeps the stored copy in sync
  // without a fresh sign-in.
  const updateAccount = (next: PortalAccount) => {
    localStorage.setItem(PORTAL_ACCOUNT_KEY, JSON.stringify(next));
    setAccount(next);
  };

  const logout = () => {
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    localStorage.removeItem(PORTAL_ACCOUNT_KEY);
    setAccount(null);
  };

  return (
    <PortalAuthContext.Provider value={{ account, isAuthenticated: !!account, isLoading: false, login, loginWithToken, updateAccount, logout }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  return ctx;
}
